// Project/Farm Data for AgriLink
// Contains location, polygon boundaries, and station positions

import { PUGLIA_OSM_SECTIONS as A3_EMILIA_OSM_SECTIONS } from './pugliaSectionsOsm.medium.js';

const getSectionCenter = (section) => {
  if (Array.isArray(section?.center) && section.center.length === 2) {
    return section.center;
  }
  if (Array.isArray(section?.polygon) && section.polygon.length > 0) {
    const lat = section.polygon.reduce((sum, p) => sum + p[0], 0) / section.polygon.length;
    const lon = section.polygon.reduce((sum, p) => sum + p[1], 0) / section.polygon.length;
    return [lat, lon];
  }
  return null;
};

const getBoundsFromSections = (sections = []) => {
  const bounds = {
    minLat: Number.POSITIVE_INFINITY,
    maxLat: Number.NEGATIVE_INFINITY,
    minLon: Number.POSITIVE_INFINITY,
    maxLon: Number.NEGATIVE_INFINITY,
  };

  const updateBounds = (point) => {
    if (!Array.isArray(point) || point.length < 2) return;
    const [lat, lon] = point;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    bounds.minLat = Math.min(bounds.minLat, lat);
    bounds.maxLat = Math.max(bounds.maxLat, lat);
    bounds.minLon = Math.min(bounds.minLon, lon);
    bounds.maxLon = Math.max(bounds.maxLon, lon);
  };

  sections.forEach((section) => {
    if (Array.isArray(section?.polygon) && section.polygon.length > 0) {
      section.polygon.forEach(updateBounds);
      return;
    }
    const center = getSectionCenter(section);
    if (center) updateBounds(center);
  });

  if (!Number.isFinite(bounds.minLat)) return null;
  return bounds;
};

const getBoundsCenter = (bounds) => {
  if (!bounds) return null;
  return [
    (bounds.minLat + bounds.maxLat) / 2,
    (bounds.minLon + bounds.maxLon) / 2,
  ];
};

const boundsToRectanglePolygon = (bounds) => {
  if (!bounds) return null;
  return [
    [bounds.maxLat, bounds.minLon],
    [bounds.maxLat, bounds.maxLon],
    [bounds.minLat, bounds.maxLon],
    [bounds.minLat, bounds.minLon],
  ];
};


const A3_EMILIA_EXCLUDED_STATION_NUMBERS = [22, 25, 26, 27, 28, 29, 30];
const A3_EMILIA_SECTIONS = A3_EMILIA_OSM_SECTIONS
  .filter((_, index) => !A3_EMILIA_EXCLUDED_STATION_NUMBERS.includes(index + 1))
  .map((section, index) => {
    const blockCode = String(index + 1).padStart(3, '0');
    return {
      ...section,
      name: `Block ${blockCode}`,
    };
  });

const A3_EMILIA_BOUNDS = getBoundsFromSections(A3_EMILIA_SECTIONS);
const A3_EMILIA_CENTER = getBoundsCenter(A3_EMILIA_BOUNDS) || [41.2652, 15.8956];
const A3_EMILIA_POLYGON = boundsToRectanglePolygon(A3_EMILIA_BOUNDS);
const A3_EMILIA_STATION_SECTION_COUNT = A3_EMILIA_SECTIONS.length;

export const PROJECTS_DATA = [
  {
    id: 'project-1',
    title: 'A1_Lazio',
    location: 'Lazio, Italy',
    status: 'In Costruzione',
    power: '1 MW',
    area: '19857 m²',
    crop: 'Grano',
    technology: 'Tracker Monoassiale',
    stationsCount: 4,
    description: 'Monitoraggio con 4 stazioni: 2 retro modulo e 2 su campo aperto. La stazione master riceve i dati via radio e li invia tramite 4G al cloud.',
    center: [42.3243, 12.4225],
    zoom: 17,
    polygon: [
      [42.3250583, 12.4216500],
      [42.3257200, 12.4238913],
      [42.3233773, 12.4228115],
      [42.3234582, 12.4223755],
    ],
    masterStation: [42.3250634, 12.4220750],
    slaveStations: [
      [42.3250884, 12.4229949],
      [42.3243494, 12.4226004],
      [42.3237260, 12.4225979]
    ]
  },
  {
    id: 'project-2',
    title: 'A2_Umbria',
    location: 'Amelia, Umbria',
    status: 'In Costruzione',
    power: '1 MW',
    area: '19545 m²',
    crop: 'Erba Medica',
    technology: 'Tracker Monoassiale',
    stationsCount: 4,
    description: 'Monitoraggio con 4 stazioni: 2 retro modulo e 2 su campo aperto. La stazione master riceve i dati via radio e li invia tramite 4G al cloud.',
    center: [42.6065, 12.3670],
    zoom: 17,
    polygon: [
      [42.6079780, 12.3667955],
      [42.6077741, 12.3661719],
      [42.6072816, 12.3662438],
      [42.6055688, 12.3675344],
      [42.6056846, 12.3677642],
      [42.6060903, 12.3679454],
      [42.6062687, 12.3679493],
      [42.6063127, 12.3681266],
      [42.6072329, 12.3672292]
    ],
    masterStation: [42.6062687, 12.3679493],
    slaveStations: [
      [42.6066903, 12.3671219],
      [42.6071879, 12.3667925],
      [42.6075735, 12.3665351]
    ]
  },
  {
    id: 'project-5',
    title: 'A3_Emilia',
    codePrefix: 'A3-EMILIA',
    location: 'Emilia-Romagna, Italy',
    status: 'Operativo',
    power: '12 MW',
    area: '~31 ha (OSM parcels)',
    crop: 'Farmland',
    technology: 'OSM parcels',
    stationsCount: A3_EMILIA_STATION_SECTION_COUNT * 2,
    excludedStationNumbers: [],
    description: 'Parcelle agricole OSM (Emilia-Romagna) con due sensori per blocco (campo aperto + retro modulo).',
    center: A3_EMILIA_CENTER,
    zoom: 15,
    polygon: A3_EMILIA_POLYGON,
    masterStation: A3_EMILIA_CENTER,
    slaveStations: [],
    sections: A3_EMILIA_SECTIONS,
  }
];

// Export project base coordinates for station generation
export const PROJECT_BASE_COORDS = PROJECTS_DATA.reduce((acc, project) => {
  acc[project.id] = { 
    lat: project.center[0], 
    lon: project.center[1] 
  };
  return acc;
}, {});

// Get project by ID
export const getProjectById = (id) => PROJECTS_DATA.find(p => p.id === id);

// Get all project IDs
export const getProjectIds = () => PROJECTS_DATA.map(p => p.id);
